const {lineString} = require('@turf/helpers');
const getBbox = require('@turf/bbox').default;
import {faBiking, faHorse, faMapSigns, faSkiing, faWater} from '@fortawesome/free-solid-svg-icons';
import {
  faChartLine,
  faShoePrints,
} from '@fortawesome/free-solid-svg-icons';
import upperFirst from 'lodash/upperFirst';
import React from 'react';
import Helmet from 'react-helmet';
import { useHistory } from 'react-router-dom';
import { useOfflineContext } from '../../../contextProviders/OfflineContext';
import useFluent from '../../../hooks/useFluent';
import {useBasicTrailDetail} from '../../../queries/trails/useBasicTrailDetail';
import {Routes, setTrailOgImageUrl} from '../../../routing/routes';
import {trailDetailLink} from '../../../routing/Utils';
import {PeakListVariants, TrailType} from '../../../types/graphQLTypes';
import {CoreItem} from '../../../types/itemTypes';
import {slopeToSteepnessClass} from '../../../utilities/trailUtils';
import DefaultError from '../../sharedComponents/DefaultError';
import MapLegend from '../../sharedComponents/detailComponents/header/MapLegend';
import SimpleHeader, { Detail } from '../../sharedComponents/detailComponents/header/SimpleHeader';
import TripsAndNotes from '../../sharedComponents/detailComponents/TripsAndNotes';
import MapRenderProp from '../../sharedComponents/MapRenderProp';
import {trailDefaultSvg} from '../../sharedComponents/svgIcons';
import DownloadGPX from './moreInformationPanel/DownloadGPX';
import TrailToRoute from './TrailToRoute';

interface Props {
  id: string;
}

const TrailDetail = (props: Props) => {
  const { id } = props;

  const getString = useFluent();
  const {offline} = useOfflineContext();
  const history = useHistory();

  const {loading, data, error} = useBasicTrailDetail(id);

  if (error && !data) {
    if (offline) {
      return null;
    }
    return <DefaultError error={error} />;
  }

  let name: string = '----';
  let subtitle: string = '----';
  let hasChildren: boolean = false;
  let trailLengthText: string = '----';
  let formattedType: string = '';
  let map: React.ReactElement<any> | null = null;
  const parentTrails: {id: string, name: string}[] = [];
  const details: Detail[] = [];
  if (data !== undefined) {
    const { trail } = data;
    if (!trail) {
      history.replace(Routes.NotFound);
      return null;
    } else {
      const {
        childrenCount, parents, locationText,
      } = trail;

      hasChildren = Boolean(childrenCount);

      const type = childrenCount ? TrailType.parentTrail : trail.type;
      const trailLength = trail.trailLength ? trail.trailLength : 0;

      trailLengthText = trailLength < 0.1
        ? getString('distance-feet-formatted', {feet: Math.round(trailLength * 5280)}) // miles to feet conversion
        : getString('directions-driving-distance', {miles: parseFloat(trailLength.toFixed(1))});
      details.push({
        text: trailLengthText,
        icon: faShoePrints,
      });

      formattedType = upperFirst(getString('global-formatted-trail-type', {type}));
      name = trail.name ? trail.name : formattedType;

      const _in = locationText && locationText.toLowerCase().includes('across') ? ' ' : ' in ';

      subtitle = hasChildren ? formattedType + _in + locationText : getString('trail-detail-subtitle', {
          type: formattedType, segment: parents.length, state: locationText,
        });
      if (parents.length) {
        parents.forEach(parent => {
          if (parent && parent.id !== null && parent.name !== null) {
            parentTrails.push({
              id: parent.id,
              name: parent.name.toLowerCase() === name.toLowerCase()
                ? parent.name + ` (${getString('trail-parent-full-trail')})`
                : parent.name,
            });
          }
        });
      }
      if (trail.avgSlope !== undefined && trail.avgSlope !== null) {
        details.push({
          text: upperFirst(slopeToSteepnessClass(trail.avgSlope)) +
            `, ${parseFloat(trail.avgSlope.toFixed(1))}° ` +
            getString('global-text-value-average-incline').toLowerCase(),
          icon: faChartLine,
        });
      }
      if (childrenCount) {
        details.push({
          text: childrenCount + ' ' + getString('global-text-value-total-segments'),
          icon: trailDefaultSvg,
          isCustomIcon: true,
        });
      }
      if (trail.allowsBikes !== null) {
        details.push({
          text: getString('trail-detail-formatted-allows-bikes', {value: trail.allowsBikes.toString()}),
          icon: faBiking,
          faded: !trail.allowsBikes,
        });
      }
      if (trail.allowsHorses !== null) {
        details.push({
          text: getString('trail-detail-formatted-allows-horses', {value: trail.allowsHorses.toString()}),
          icon: faHorse,
          faded: !trail.allowsHorses,
        });
      }
      if (trail.skiTrail ||
        (name.toLowerCase() + ' ').includes(' ski ') ||
        name.toLowerCase().includes(' skiing ')
        ) {
        details.push({
          text: getString('trail-detail-ski-trail'),
          icon: faSkiing,
        });
      }
      if (trail.waterCrossing !== null) {
        details.push({
          text: getString('trail-detail-water-crossing') + ': ' + trail.waterCrossing,
          icon: faWater,
        });
      }
      if (!childrenCount && !offline) {
        details.push({
          text: <DownloadGPX id={trail.id} />,
          icon: null,
        });
      }
      if (trail.line && trail.line.length && trail.center) {
        const bbox = getBbox(lineString(trail.line));
        map = (
          <>
            <MapRenderProp
              id={trail.id}
              bbox={bbox}
            />
            <TrailToRoute
              id={trail.id}
              name={trail.name}
              type={trail.type}
              line={trail.line}
              center={trail.center}
            />
          </>
        );
      }
      if (trail.bbox && trail.bbox.length === 4) {
        map = (
          <MapRenderProp
            id={trail.id}
            bbox={trail.bbox}
          />
        );
      }
    }
  }

  const completionLeged = hasChildren ? (
      <MapLegend
        type={PeakListVariants.standard}
        hasTrails={true}
        hasMountains={false}
        hasCampsites={false}
      />
  ) : null;

  const metaDescription = data && data.trail
    ? getString('meta-data-trail-detail-description', {
      name,
      location: data.trail.locationText,
      length: trailLengthText,
      type: formattedType.toLowerCase(),
    })
    : null;
  const metaTitle = data && data.trail ? getString('meta-data-detail-default-title', {
      title: name, type: '',
    }) : '';
  const metaData = metaTitle && metaDescription && data && data.trail ? (
    <Helmet>
      <title>{metaTitle}</title>
      <meta
        name='description'
        content={metaDescription}
      />
      <meta property='og:title' content={metaTitle} />
      <meta
        property='og:description'
        content={metaDescription}
      />
      <link rel='canonical' href={process.env.REACT_APP_DOMAIN_NAME + trailDetailLink(id)} />
      <meta property='og:image' content={setTrailOgImageUrl(id)} />
    </Helmet>
  ) : null;

  return (
    <>
      {metaData}
      {completionLeged}
      <SimpleHeader
        id={id}
        loading={loading}
        title={name}
        subtitle={subtitle}
        customIcon={!hasChildren}
        icon={hasChildren ? faMapSigns : trailDefaultSvg}
        authorId={null}
        type={CoreItem.trail}
        isParentTrail={hasChildren}
        primaryDetails={details}
        parentTrails={parentTrails}
      />
      <TripsAndNotes
        id={id}
        name={name}
        item={CoreItem.trail}
        notesOnly={hasChildren}
      />
      {map}
    </>
  );
};

export default TrailDetail;
